import { useState, useEffect } from "react";
import {
  Table,
  Button,
  Card,
  Row,
  Col,
  Badge,
  Form,
  Spinner,
  Alert,
} from "react-bootstrap";
import { FaBackward, FaUserPlus, FaEdit } from "react-icons/fa";
import { PopUpModalButton } from "../../../../../components/basic/ModalComponent";
import AddPartyForm from "./AddPartyForm";
import AddArbitratorForm from "./AddArbitratorForm";
import { getAuthUserlocal } from "../../../../../store/services";
import axios from "axios";
import { getURLbyEndPointV2 } from "../../../../../store/api";
import { countriesList } from "../../../../../store/AllListData";
import { getBindingDisputeTrack } from "./services/bindingDisputeService";

const industries = [
  "Agriculture",
  "Automotive & Heavy Industry",
  "Construction & Engineering",
  "Commodities Trading",
  "Energy & Power",
  "Fashion & Luxury Goods",
  "Gas",
  "High-tech Products",
  "Infrastructure",
  "Media & Entertainment",
  "Mining & Metals",
  "Oil & Petroleum",
  "Real Estate",
  "Pharmaceutical & Biotech",
  "Shipbuilding",
  "Telecommunications",
  "Transportation",
  "Logistics & Shipping",
  "Agri-Tech",
  "Machineries",
  "Other",
];

const natureOfBusinessOptions = [
  "Merchant Exporter",
  "Trading",
  "Banking",
  "Service Provider",
  "Insurance",
  "IT Sector",
  "Manufacturing",
  "Consulting",
  "Logistics",
  "Other",
];

const BindingDisputeDetails = ({
  selectedData: initialSelectedData,
  onBack,
  onRefresh,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [selectedData, setSelectedData] = useState(initialSelectedData);

  const [showAddPartyForm, setShowAddPartyForm] = useState(false);
  const [additionalParties, setAdditionalParties] = useState([]);
  const [showAddArbitratorForm, setShowAddArbitratorForm] = useState(false);
  const [currentRole, setCurrentRole] = useState("");

  const user = getAuthUserlocal();

  // Fetch fresh data on mount to ensure timetable docs are up to date
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getBindingDisputeTrack();
        if (response.status && Array.isArray(response.data)) {
          const freshData = response.data.find(
            (d) => d._id === initialSelectedData._id
          );
          if (freshData) {
            setSelectedData(freshData);
          }
        }
      } catch (error) {
        console.error("Failed to refresh dispute details", error);
      }
    };
    fetchData();
  }, [initialSelectedData._id]);

  // Fee calculation functions
  const calculateAdminFee = (amount) => {
    if (amount <= 75000) return 1375;
    if (amount <= 200000) return 2000 + (amount - 75000) * 0.015;
    if (amount <= 700000) return 2800 + (amount - 200001) * 0.014;
    if (amount <= 1200000) return 7000 + (amount - 700001) * 0.013;
    if (amount <= 2200000) return 10000 + (amount - 1200001) * 0.01;
    if (amount <= 6200000) return 12000 + (amount - 2200001) * 0.008;
    if (amount <= 12500000) return 20000 + (amount - 6200001) * 0.0075;
    return 50000 + (amount - 12500001) * 0.005;
  };

  const calculateArbitratorFee = (amount) => {
    if (amount <= 75000) return 1218;
    if (amount <= 200000) return 2343 + (amount - 75001) * 0.03;
    if (amount <= 700000) return 4218 + (amount - 200001) * 0.01;
    if (amount <= 1200000) return 9218 + (amount - 700001) * 0.01;
    if (amount <= 2200000) return 15468 + (amount - 1200001) * 0.0075;
    if (amount <= 6200000) return 24843 + (amount - 2200001) * 0.005;
    return null; // Contact Chamber Desk
  };

  useEffect(() => {
    if (initialSelectedData) {
      setSelectedData(initialSelectedData);

      const ps = initialSelectedData.paymentSchedule || {};
      const flexible = ps.flexiblePlanInstallments || [];
      const schedules = ps.arbitratorFeeDetails?.schedules || [];

      const scheduleMap = {};
      schedules.forEach((s) => {
        if (s.key.includes("ONE")) scheduleMap.scheduleOne = s.amount;
        if (s.key.includes("TWO")) scheduleMap.scheduleTwo = s.amount;
        if (s.key.includes("THREE")) scheduleMap.scheduleThree = s.amount;
        if (s.key.includes("FINAL")) scheduleMap.scheduleFinal = s.amount;
      });

      const feeDetails = {
        memberType: initialSelectedData.memberType || "MEMBER",
        filingFee: ps.filingFee || 0,
        originalAdminFee: ps.adminFee || 0,
        arbitratorFee: { total: ps.arbitratorFee || 0, schedules: scheduleMap },
        flexiblePlan: {
          firstInstallment: flexible[0]?.amount || 0,
          secondInstallment: flexible[1]?.amount || 0,
          thirdInstallment: flexible[2]?.amount || 0,
        },
        selectedFeePlan: ps.paymentPlan || "BASIC",
        totalFee: ps.totalFee || 0,
      };

      setFormData({
        ...initialSelectedData,
        notes: initialSelectedData.notes || "",
        feePlanDetails: feeDetails,
      });

      setAdditionalParties([
        ...(initialSelectedData.claimantOtherPartyIds || []),
        ...(initialSelectedData.respondentOtherPartyIds || []),
      ]);
    }
  }, [initialSelectedData]);

  // Recalculate fees when quantum of claim changes
  useEffect(() => {
    if (isEditing && formData.quantumOfClaim) {
      const amount = parseInt(formData.quantumOfClaim) || 0;
      const adminFee = Math.round(calculateAdminFee(amount) * 100) / 100;
      const arbitratorFeeRaw = calculateArbitratorFee(amount);
      const arbitratorFee =
        arbitratorFeeRaw !== null
          ? Math.round(arbitratorFeeRaw * 100) / 100
          : null;

      const baseTotal =
        (formData.feePlanDetails?.filingFee || 0) +
        (adminFee || 0) +
        (arbitratorFee || 0);
      const flexibleEnabled = baseTotal > 15000;

      const selectedFeePlan =
        formData.feePlanDetails?.selectedFeePlan || "BASIC";
      const effectiveAdminFee =
        selectedFeePlan === "STANDARD"
          ? Math.round(adminFee * 0.9 * 100) / 100
          : selectedFeePlan === "FLEXIBLE"
          ? Math.round(adminFee * 0.4 * 100) / 100
          : adminFee;

      const arbitratorFeeScheduleOne = arbitratorFee
        ? Math.round(arbitratorFee * 0.25 * 100) / 100
        : 0;
      const arbitratorFeeScheduleTwo = arbitratorFee
        ? Math.round(arbitratorFee * 0.25 * 100) / 100
        : 0;
      const arbitratorFeeScheduleThree = arbitratorFee
        ? Math.round(arbitratorFee * 0.35 * 100) / 100
        : 0;
      const arbitratorFeeScheduleFinal = arbitratorFee
        ? Math.round(arbitratorFee * 0.15 * 100) / 100
        : 0;
      const arbitratorFeePayableNow =
        selectedFeePlan === "FLEXIBLE"
          ? arbitratorFeeScheduleOne
          : arbitratorFee || 0;

      const firstInstallment =
        selectedFeePlan === "FLEXIBLE"
          ? Math.round(adminFee * 0.4 * 100) / 100
          : 0;
      const secondInstallment =
        selectedFeePlan === "FLEXIBLE"
          ? Math.round(adminFee * 0.4 * 100) / 100
          : 0;
      const thirdInstallment =
        selectedFeePlan === "FLEXIBLE"
          ? Math.round(adminFee * 0.2 * 100) / 100
          : 0;

      const totalFee =
        Math.round(
          ((formData.feePlanDetails?.filingFee || 0) +
            (effectiveAdminFee || 0) +
            (arbitratorFeePayableNow || 0)) *
            100
        ) / 100;

      setFormData((prev) => ({
        ...prev,
        feePlanDetails: {
          ...prev.feePlanDetails,
          adminFee: { effective: effectiveAdminFee },
          originalAdminFee: adminFee,
          arbitratorFee: {
            total: arbitratorFee || 0,
            payableNow: arbitratorFeePayableNow,
            schedules: {
              scheduleOne: arbitratorFeeScheduleOne,
              scheduleTwo: arbitratorFeeScheduleTwo,
              scheduleThree: arbitratorFeeScheduleThree,
              scheduleFinal: arbitratorFeeScheduleFinal,
            },
          },
          flexiblePlan: {
            firstInstallment,
            secondInstallment,
            thirdInstallment,
          },
          totalFee,
          isFlexibleEnabled: flexibleEnabled,
          selectedFeePlan,
        },
      }));
    }
  }, [formData.quantumOfClaim, isEditing]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (field) => (e) => {
    if (e.target.files[0])
      setFormData({ ...formData, [field]: e.target.files[0] });
  };

  const handleSave = async () => {
    if (loading) return;
    setLoading(true);
    const submitData = new FormData();

    Object.keys(formData).forEach((key) => {
      if (formData[key] !== selectedData[key]) {
        if (formData[key] instanceof File) {
          submitData.append(key, formData[key]);
        } else if (key !== "feePlanDetails") {
          submitData.append(key, formData[key] || "");
        }
      }
    });

    if (formData.feePlanDetails) {
      submitData.append(
        "feePlanDetails",
        JSON.stringify(formData.feePlanDetails)
      );
    }

    try {
      const res = await axios.patch(
        getURLbyEndPointV2("updateBindingDispute") + selectedData._id,
        submitData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (res.data.status) {
        alert("Dispute updated & resubmitted successfully!");
        onRefresh?.();
        setIsEditing(false);
      } else {
        alert(res.data.message || "Update failed");
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update");
    } finally {
      setLoading(false);
    }
  };

  const renderValue = (value) => (value ? value : "N/A");
  const renderCurrency = (amount) =>
    amount != null
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(amount)
      : "N/A";

  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "warning";
      case "approved":
        return "success";
      case "rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  const getDocName = (key) => {
    const names = {
      "E-C1": "Appointment of Arbitrator (Sole)",
      "E-C1-Arb1": "Appointment Letter - Arbitrator 1",
      "E-C1-Arb2": "Appointment Letter - Arbitrator 2",
      "E-C1-Arb3": "Appointment Letter - Arbitrator 3",
      "E-C2": "Claimant to Arbitrator",
      "E-C3": "Respondent to Arbitrator",
      "E-R1": "Statement of Defense",
      "E-D1": "Written Claimant Statement (E-D1)",
      "E-D2": "Written Statement (E-D2)",
      "E-G1": "Hearing Notice Of claimant (E-G1)",
      "E-G2": "Hearing Notice Of Respondent (E-G2)",
      "E-G-Arb1": "Hearing Notice - Arbitrator 1",
      "E-G-Arb2": "Hearing Notice - Arbitrator 2",
      "E-G-Arb3": "Hearing Notice - Arbitrator 3",
      ToTribunal: "Hearing Notice - Sole Arbitrator/Tribunal",
      "E-I1": "Checklist to Arbitrators (E-I1)",
      "E-I2": "Feedback Form (E-I2)",
      "E-I2-Claimant": "Feedback Form - Claimant (E-I2-Claimant)",
      "E-I2-Respondent": "Feedback Form - Respondent (E-I2-Respondent)",
      "E-I3": "Copy of Award (in writing) (E-I3)",
      "E-I3-Arb1": "Copy of Award - Arbitrator 1",
      "E-I3-Arb2": "Copy of Award - Arbitrator 2",
      "E-I3-Arb3": "Copy of Award - Arbitrator 3",
      Enquiry: "Enquiry by Arbitrator in Writing",
      CounterClaim: "Counter Claim (by Respondent/Claimant)",
      Production: "Production of Additional Information & Documents",
    };
    return names[key] || key;
  };

  // === RENDER TIMETABLE DOCUMENTS (FOR CLIENTS/PARTIES) ===
  const renderTimeTableDocuments = () => {
    if (!selectedData.timeTable || !selectedData.timeTable.sections)
      return null;

    const sections = selectedData.timeTable.sections;
    const allDocs = [];

    sections.forEach((section) => {
      if (section.documents) {
        Object.keys(section.documents).forEach((docKey) => {
          let shouldShow = true;

          // HIDE arbitrator appointments from parties
          // Parties (Claimant, Respondent, etc.) should generally NOT see the private appointment letters (contracts).
          // However, they SHOULD see intimations (E-C2, E-C3).
          // "E-C1-Arb" covers Tribunal appointment letters. "E-C1" covers Sole Arbitrator letter.

          const nameToCheck = getDocName(docKey).toLowerCase();

          // STRICT FILTER: Hide E-C1 family (Appointment Letters)
          if (docKey.startsWith("E-C1")) {
            shouldShow = false;
          }

          // But explicitly ALLOW E-C2 and E-C3 (Notifications to Parties) if they were caught by startsWith E-C1 (unlikely, but safe)
          // Actually E-C2 starts with E-C2, so it escapes the above check.

          // SECONDARY FILTER: Check for "Appointment" + "Arbitrator" in name,
          // BUT exclude Claimant/Respondent notifications from this hide logic.
          if (
            nameToCheck.includes("appointment") &&
            nameToCheck.includes("arbitrator") &&
            !nameToCheck.includes("claimant") &&
            !nameToCheck.includes("respondent")
          ) {
            shouldShow = false;
          }

          // Hearing Notice Visibility Logic
          const role = selectedData.accessRole;

          // E-G1: Only for Claimant (Hidden from Respondent, Other Parties, Arbitrator)
          if (docKey === "E-G1" && role !== "CLAIMANT") {
            shouldShow = false;
          }

          // E-G2: Only for Respondent and Other Parties (Hidden from Claimant, Arbitrator)
          if (docKey === "E-G2") {
            const isRespondentSide = role === "RESPONDENT";
            if (!isRespondentSide) {
              shouldShow = false;
            }
          }

          // E-I2-Claimant: Only for Claimant
          if (docKey === "E-I2-Claimant" && role !== "CLAIMANT") {
            shouldShow = false;
          }

          // E-I2-Respondent: Only for Respondent/Other Parties
          if (docKey === "E-I2-Respondent") {
            const isRespondentSide = role === "RESPONDENT";
            if (!isRespondentSide) {
              shouldShow = false;
            }
          }

          // HIDE Arbitrator Checklist (E-I1) from Clients (Claimant/Respondent)
          if (docKey === "E-I1") {
            shouldShow = false;
          }

          // HIDE Copies of Award (CopiesOfAward) from Clients (as per request "show in arbitrator only")
          if (docKey === "CopiesOfAward") {
            shouldShow = false;
          }

          // HIDE Arbitrator Specific Notices and Awards from Clients
          // "Arb" implies a specific document for an arbitrator (E-G-Arb1, E-I3-Arb1, etc.)
          if (docKey.includes("Arb") || docKey.startsWith("E-I3")) {
            shouldShow = false;
          }

          if (shouldShow) {
            allDocs.push({
              section: section.sectionIndex,
              name: getDocName(docKey),
              url: section.documents[docKey],
              key: docKey,
            });
          }
        });
      }
    });

    if (allDocs.length === 0) return null;

    return (
      <Card className="mb-3">
        <Card.Header>
          <strong>Time Table Documents</strong>
        </Card.Header>
        <Card.Body>
          <Table bordered size="sm" responsive>
            <thead>
              <tr>
                <th>Section</th>
                <th>Document Name</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {allDocs.map((doc, i) => (
                <tr key={i}>
                  <td>
                    {doc.section === 0 ? "Section A" : `Section ${doc.section}`}
                  </td>
                  <td>{doc.name}</td>
                  <td>
                    <PopUpModalButton title="View" fileLink={doc.url} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  };

  const renderFeePlanDetails = () => {
    const sourceData = isEditing
      ? formData.feePlanDetails
      : selectedData.paymentSchedule;

    if (!sourceData) return null;

    const feePlan =
      sourceData.selectedFeePlan || sourceData.paymentPlan || "None";

    let flexibleInstallments = [];
    let arbitratorSchedules = [];

    if (isEditing) {
      if (sourceData.flexiblePlan) {
        flexibleInstallments = [
          {
            key: "First Installment",
            amount: sourceData.flexiblePlan.firstInstallment,
            isPaid: false,
            document: null,
          },
          {
            key: "Second Installment",
            amount: sourceData.flexiblePlan.secondInstallment,
            isPaid: false,
            document: null,
          },
          {
            key: "Third Installment",
            amount: sourceData.flexiblePlan.thirdInstallment,
            isPaid: false,
            document: null,
          },
        ];
      }
      if (sourceData.arbitratorFee?.schedules) {
        arbitratorSchedules = [
          {
            key: "Schedule One",
            amount: sourceData.arbitratorFee.schedules.scheduleOne,
            isPaid: false,
            document: null,
          },
          {
            key: "Schedule Two",
            amount: sourceData.arbitratorFee.schedules.scheduleTwo,
            isPaid: false,
            document: null,
          },
          {
            key: "Schedule Three",
            amount: sourceData.arbitratorFee.schedules.scheduleThree,
            isPaid: false,
            document: null,
          },
          {
            key: "Final Schedule",
            amount: sourceData.arbitratorFee.schedules.scheduleFinal,
            isPaid: false,
            document: null,
          },
        ];
      }
    } else {
      flexibleInstallments = sourceData.flexiblePlanInstallments || [];
      arbitratorSchedules = sourceData.arbitratorFeeDetails?.schedules || [];
    }

    return (
      <>
        {renderValue(feePlan) !== "N/A" && (
          <p className="mb-2">
            <strong>Fee Plan Type:</strong> {feePlan}
          </p>
        )}

        {feePlan === "FLEXIBLE" && flexibleInstallments.length > 0 && (
          <>
            <h6 className="fw-bold mt-4 mb-3">Flexible Plan Installments</h6>
            <Table bordered size="sm" responsive>
              <thead>
                <tr>
                  <th>Installment</th>
                  <th>Amount</th>
                  {/* <th>Status</th>
                  <th>Receipt</th> */}
                </tr>
              </thead>
              <tbody>
                {flexibleInstallments.map((installment, index) => (
                  <tr key={index}>
                    <td>{renderValue(installment.key)}</td>
                    <td>{renderCurrency(installment.amount)}</td>
                    {/* <td>{installment.isPaid ? "Paid" : "Unpaid"}</td>
                    <td>
                      {installment.document ? (
                        <PopUpModalButton
                          title="View"
                          fileLink={installment.document}
                        />
                      ) : (
                        "N/A"
                      )}
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}

        {arbitratorSchedules.length > 0 && (
          <>
            <h6 className="fw-bold mt-4 mb-3">Arbitrator Fee Schedules</h6>
            <Table bordered size="sm" responsive>
              <thead>
                <tr>
                  <th>Schedule</th>
                  <th>Amount</th>
                  {/* <th>Status</th>
                  <th>Receipt</th> */}
                </tr>
              </thead>
              <tbody>
                {arbitratorSchedules.map((schedule, index) => (
                  <tr key={index}>
                    <td>{renderValue(schedule.key)}</td>
                    <td>{renderCurrency(schedule.amount)}</td>
                    {/* <td>{schedule.isPaid ? "Paid" : "Unpaid"}</td>
                    <td>
                      {schedule.document ? (
                        <PopUpModalButton
                          title="View"
                          fileLink={schedule.document}
                        />
                      ) : (
                        "N/A"
                      )}
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </>
    );
  };

  const fileFields = [
    { key: "companyRegistrationDoc", label: "Company Registration Document" },
    { key: "agreementContract", label: "Agreement Contract" },
    { key: "arbitrationForm", label: "Arbitration Form" },
    {
      key: "correspondenceWithSupplier",
      label: "Correspondence with Supplier",
    },
    { key: "commercialInvoice", label: "Commercial Invoice" },
    { key: "inspectionReports", label: "Inspection Reports" },
    { key: "correspondenceWithAgent", label: "Correspondence with Agent" },
    { key: "correspondenceWithCarrier", label: "Correspondence with Carrier" },
    { key: "photographs", label: "Photographs" },
    { key: "legalActionDocuments", label: "Legal Action Documents" },
  ];

  if (!selectedData) {
    return (
      <div className="text-center mt-5">
        <h5>No Dispute Data Available</h5>
        <Button variant="secondary" onClick={onBack}>
          <FaBackward /> Back
        </Button>
      </div>
    );
  }

  const isRejected = selectedData.isApproved === "Rejected";
  const isPaymentDone = selectedData.paymentSchedule?.paymentCompletedAt;
  const paymentLinkSent = selectedData.paymentSchedule?.paymentLinkSent;
  const paymentCompletedAt = selectedData.paymentSchedule?.paymentCompletedAt;
  const canEdit = !isPaymentDone && !paymentLinkSent && !paymentCompletedAt;

  return (
    <div>
      <div className="d-flex gap-2 justify-content-between align-items-center mb-4">
        <h4>Application No: {selectedData.appNo}</h4>
        <div>
          {canEdit && !isEditing && (
            <Button
              variant="warning"
              onClick={() => setIsEditing(true)}
              className="me-2"
            >
              <FaEdit /> Edit & Resubmit
            </Button>
          )}
          <Button variant="secondary" onClick={onBack}>
            <FaBackward /> Back
          </Button>
        </div>
      </div>

      {isRejected && selectedData.remark && (
        <Alert variant="danger" className="mb-4">
          <strong>Rejection Reason:</strong> {selectedData.remark}
        </Alert>
      )}

      <Form>
        {/* Claimant & Business */}
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-4">
              <Col xs={12} md={6}>
                <h5 className="fw-bold mb-3 text-primary">
                  Claimant Information
                </h5>
                {[
                  "claimantName",
                  "address",
                  "city",
                  "zipCode",
                  "phone",
                  "panNo",
                  "email",
                ].map((f) => (
                  <Form.Group key={f} className="mb-2">
                    <Form.Label className="fs-6 fw-semibold">
                      {f.replace(/([A-Z])/g, " $1").trim()}
                    </Form.Label>
                    {isEditing ? (
                      <Form.Control
                        name={f}
                        value={formData[f] || ""}
                        onChange={handleChange}
                      />
                    ) : (
                      <div className="form-control-plaintext">
                        {renderValue(selectedData[f])}
                      </div>
                    )}
                  </Form.Group>
                ))}
                {/* Country Dropdown */}
                <Form.Group className="mb-2">
                  <Form.Label className="fs-6 fw-semibold">Country</Form.Label>
                  {isEditing ? (
                    <Form.Select
                      name="country"
                      value={formData.country || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select Country</option>
                      {countriesList.map((value, index) => (
                        <option key={index} value={value}>
                          {value}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <div className="form-control-plaintext">
                      {renderValue(selectedData.country)}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <h5 className="fw-bold mb-3 text-primary">Business Details</h5>
                {/* Nature of Business Dropdown */}
                <Form.Group className="mb-2">
                  <Form.Label className="fs-6 fw-semibold">
                    Nature of Business
                  </Form.Label>
                  {isEditing ? (
                    <Form.Select
                      name="natureOfBusiness"
                      value={formData.natureOfBusiness || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      {natureOfBusinessOptions.map((value, index) => (
                        <option key={index} value={value}>
                          {value}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <div className="form-control-plaintext">
                      {renderValue(selectedData.natureOfBusiness)}
                    </div>
                  )}
                </Form.Group>
                {/* Industry Dropdown */}
                <Form.Group className="mb-2">
                  <Form.Label className="fs-6 fw-semibold">Industry</Form.Label>
                  {isEditing ? (
                    <Form.Select
                      name="industry"
                      value={formData.industry || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      {industries.map((value, index) => (
                        <option key={index} value={value}>
                          {value}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <div className="form-control-plaintext">
                      {renderValue(selectedData.industry)}
                    </div>
                  )}
                </Form.Group>
                {/* aecciRegNo & memberType → Disabled */}
                <Form.Group className="mb-2">
                  <Form.Label className="fs-6 fw-semibold">
                    AECCI Registration No
                  </Form.Label>
                  <Form.Control
                    value={selectedData.aecciRegNo || ""}
                    disabled
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="fs-6 fw-semibold">
                    Member Type
                  </Form.Label>
                  <Form.Control
                    value={selectedData.memberType || ""}
                    disabled
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        {/* Respondent & Dispute */}
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-4">
              <Col xs={12} md={6}>
                <h5 className="fw-bold mb-3 text-primary">
                  Respondent Information
                </h5>
                {[
                  "respondentCompanyName",
                  "respondentRepresentativeName",
                  "respondentEmail",
                  "respondentAddress",
                  "respondentState",
                  "respondentZipCode",
                  "respondentPanNo",
                  "respondentWebsite",
                ].map((f) => (
                  <Form.Group key={f} className="mb-2">
                    <Form.Label className="fs-6 fw-semibold">
                      {f
                        .replace("respondent", "")
                        .replace(/([A-Z])/g, " $1")
                        .trim() || "Website"}
                    </Form.Label>
                    {isEditing ? (
                      <Form.Control
                        name={f}
                        value={formData[f] || ""}
                        onChange={handleChange}
                      />
                    ) : (
                      <div className="form-control-plaintext">
                        {f === "respondentWebsite" && selectedData[f] ? (
                          <a
                            href={selectedData[f]}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {selectedData[f]}
                          </a>
                        ) : (
                          renderValue(selectedData[f])
                        )}
                      </div>
                    )}
                  </Form.Group>
                ))}
                {/* Respondent Country Dropdown */}
                <Form.Group className="mb-2">
                  <Form.Label className="fs-6 fw-semibold">Country</Form.Label>
                  {isEditing ? (
                    <Form.Select
                      name="respondentCountry"
                      value={formData.respondentCountry || ""}
                      onChange={handleChange}
                    >
                      <option value="">Select Country</option>
                      {countriesList.map((value, index) => (
                        <option key={index} value={value}>
                          {value}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <div className="form-control-plaintext">
                      {renderValue(selectedData.respondentCountry)}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <h5 className="fw-bold mb-3 text-primary">Dispute Details</h5>
                {/* Date fields with datepicker */}
                {["date", "agreementStartDate", "disputeStartDate"].map((f) => (
                  <Form.Group key={f} className="mb-2">
                    <Form.Label className="fs-6 fw-semibold">
                      {f.replace(/([A-Z])/g, " $1").trim()}
                    </Form.Label>
                    {isEditing ? (
                      <Form.Control
                        type="date"
                        name={f}
                        value={formData[f] || ""}
                        onChange={handleChange}
                      />
                    ) : (
                      <div className="form-control-plaintext">
                        {renderValue(selectedData[f])}
                      </div>
                    )}
                  </Form.Group>
                ))}
                {[
                  "place",
                  "product",
                  "commercialInvoiceAmount",
                  "amountOfActivity",
                ].map((f) => (
                  <Form.Group key={f} className="mb-2">
                    <Form.Label className="fs-6 fw-semibold">
                      {f.replace(/([A-Z])/g, " $1").trim()}
                    </Form.Label>
                    {isEditing ? (
                      <Form.Control
                        name={f}
                        value={formData[f] || ""}
                        onChange={handleChange}
                      />
                    ) : (
                      <div className="form-control-plaintext">
                        {f.includes("Amount")
                          ? renderCurrency(selectedData[f])
                          : renderValue(selectedData[f])}
                      </div>
                    )}
                  </Form.Group>
                ))}
                {/* Quantum of Claim with real-time calculation */}
                <Form.Group className="mb-2">
                  <Form.Label className="fs-6 fw-semibold">
                    Quantum of Claim
                  </Form.Label>
                  {isEditing ? (
                    <Form.Control
                      name="quantumOfClaim"
                      value={formData.quantumOfClaim || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <div className="form-control-plaintext">
                      {renderCurrency(selectedData.quantumOfClaim)}
                    </div>
                  )}
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="fs-6 fw-semibold">
                    Amount in Dispute
                  </Form.Label>
                  {isEditing ? (
                    <Form.Control
                      name="amountInDispute"
                      value={formData.amountInDispute || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <div className="form-control-plaintext">
                      {renderCurrency(selectedData.amountInDispute)}
                    </div>
                  )}
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-6 fw-semibold">
                    Dispute Description
                  </Form.Label>
                  {isEditing ? (
                    <Form.Control
                      as="textarea"
                      rows={5}
                      name="disputeDescription"
                      value={formData.disputeDescription || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    <div className="form-control-plaintext">
                      {renderValue(selectedData.disputeDescription)}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        {/* Additional Parties */}
        {(selectedData.claimantOtherPartyIds?.length > 0 ||
          selectedData.respondentOtherPartyIds?.length > 0) && (
          <Card className="mb-3">
            <Card.Header>
              <strong>Additional Parties</strong>
            </Card.Header>
            <Card.Body>
              <Table bordered size="sm" responsive className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Assigned To</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Mobile</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedData.claimantOtherPartyIds || []).map(
                    (party, idx) => (
                      <tr key={`claimant-party-${idx}`}>
                        <td>Claimant</td>
                        <td>{renderValue(party.name)}</td>
                        <td>{renderValue(party.email)}</td>
                        <td>{renderValue(party.mobile)}</td>
                      </tr>
                    )
                  )}
                  {(selectedData.respondentOtherPartyIds || []).map(
                    (party, idx) => (
                      <tr key={`respondent-party-${idx}`}>
                        <td>Respondent</td>
                        <td>{renderValue(party.name)}</td>
                        <td>{renderValue(party.email)}</td>
                        <td>{renderValue(party.mobile)}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {/* Registered Respondent User Separate Card */}
        {selectedData.respondentId && (
          <Card className="mb-3">
            <Card.Header>
              <strong>Registered Respondent User</strong>
            </Card.Header>
            <Card.Body>
              <Table bordered size="sm" responsive className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Statement</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {renderValue(
                        `${selectedData.respondentId.firstName} ${selectedData.respondentId.surName}`
                      )}
                    </td>
                    <td>{renderValue(selectedData.respondentId.email)}</td>
                    <td>
                      {selectedData.claimantStatement ? (
                        <PopUpModalButton
                          title="View Detail"
                          fileLink={selectedData.claimantStatement}
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}
        {/* Financial Details */}
        <Card className="mb-3">
          <Card.Header>
            <strong>Financial Details</strong>
          </Card.Header>
          <Card.Body>
            <Row className="g-4 mb-3">
              <Col xs={12} md={6}>
                <p className="mb-2">
                  <strong>Quantum of Claim:</strong>{" "}
                  {renderCurrency(
                    formData.quantumOfClaim || selectedData.quantumOfClaim
                  )}
                </p>
              </Col>
              <Col xs={12} md={6}>
                <p className="mb-2">
                  <strong>Amount in Dispute:</strong>{" "}
                  {renderCurrency(
                    formData.amountInDispute || selectedData.amountInDispute
                  )}
                </p>
              </Col>
            </Row>

            <div className="table-responsive mb-3">
              <Table bordered size="sm">
                <tbody>
                  <tr>
                    <td>Filing Fee</td>
                    <td>
                      {renderCurrency(
                        isEditing
                          ? formData.feePlanDetails?.filingFee
                          : selectedData.paymentSchedule?.filingFee
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>Admin Fee</td>
                    <td>
                      {renderCurrency(
                        isEditing
                          ? formData.feePlanDetails?.adminFee?.effective ||
                              formData.feePlanDetails?.adminFee
                          : selectedData.paymentSchedule?.adminFee
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>Arbitrator Fee (Payable Now)</td>
                    <td>
                      {renderCurrency(
                        isEditing
                          ? formData.feePlanDetails?.arbitratorFee?.payableNow
                          : selectedData.paymentSchedule?.arbitratorFeeDetails
                              ?.payableNow
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>Total Fee</td>
                    <td>
                      {renderCurrency(
                        isEditing
                          ? formData.feePlanDetails?.totalFee
                          : selectedData.paymentSchedule?.totalFee
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>

            {/* Fee Plan Details */}
            {renderFeePlanDetails()}
          </Card.Body>
        </Card>
        {/* Uploaded Documents */}
        <Card className="mb-3">
          <Card.Header>
            <strong>Uploaded Documents</strong>
          </Card.Header>
          <Card.Body>
            <Table bordered size="sm">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Current</th>
                  {isEditing && <th>New Upload</th>}
                </tr>
              </thead>
              <tbody>
                {fileFields.map(({ key, label }) => (
                  <tr key={key}>
                    <td>{label}</td>
                    <td>
                      {selectedData[key] ? (
                        <PopUpModalButton
                          title="View"
                          fileLink={selectedData[key]}
                        />
                      ) : (
                        "Not uploaded"
                      )}
                    </td>
                    {isEditing && (
                      <td>
                        <Form.Control
                          type="file"
                          onChange={handleFileChange(key)}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
        {/* TIMETABLE DOCUMENTS (NEW SECTION) */}
        {renderTimeTableDocuments()}
        {/* Status & Notes */}
        <Card className="mb-3">
          <Card.Header>
            <strong>Status & Notes</strong>
          </Card.Header>
          <Card.Body>
            <p className="mb-2">
              <strong>Status:</strong>{" "}
              <Badge
                bg={getStatusVariant(selectedData.status)}
                className="ms-2"
              >
                {selectedData.status}
              </Badge>
            </p>
            <p className="mb-2">
              <strong>Approval Status:</strong>{" "}
              {selectedData.isApproved || "Pending"}
            </p>
            <Form.Group>
              <Form.Label>
                <strong>Notes</strong>
              </Form.Label>
              {isEditing ? (
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="notes"
                  value={formData.notes || ""}
                  onChange={handleChange}
                />
              ) : (
                <div className="form-control-plaintext">
                  {renderValue(selectedData.notes)}
                </div>
              )}
            </Form.Group>
          </Card.Body>
        </Card>
        {/* READ-ONLY SECTIONS */}
        {selectedData.paymentSchedule?.paymentCompletedAt &&
          selectedData.refNo && (
            <Card className="mb-3">
              <Card.Header>
                <strong>Appointed Arbitrators</strong>
              </Card.Header>
              <Card.Body>
                {selectedData.appointedArbitratorIds &&
                selectedData.appointedArbitratorIds.length > 0 ? (
                  <div>
                    <p className="mb-3">
                      <strong>Arbitrator Type:</strong>{" "}
                      {selectedData.appointedArbitratorType || "Not specified"}
                    </p>
                    <div className="list-group">
                      {selectedData.appointedArbitratorIds.map(
                        (arbitrator, index) => (
                          <div key={arbitrator._id} className="list-group-item">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <h6 className="mb-1">
                                  {arbitrator.companyName}
                                </h6>
                                <small>
                                  {arbitrator.firstName} {arbitrator.surName}
                                </small>
                                {/* <div>
                                  <small className="text-muted">
                                    {arbitrator.email}
                                  </small>
                                </div> */}
                              </div>
                              <Badge bg="info">Arbitrator {index + 1}</Badge>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-muted">
                    No arbitrators appointed yet
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        {/* Additional Parties - Always Visible */}
        <Card className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <strong>Additional Parties</strong>
            {selectedData.accessRole !== "ARBITRATOR" && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowAddPartyForm(true)}
              >
                <FaUserPlus className="me-1" /> Add Party
              </Button>
            )}
          </Card.Header>
          <Card.Body>
            {additionalParties.length > 0 ? (
              <Table bordered size="sm" responsive>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Name</th>
                    <th>Company</th>
                    <th>Country</th>
                    <th>Status</th>
                    <th>docs</th>
                  </tr>
                </thead>
                <tbody>
                  {additionalParties.map((p, i) => (
                    <tr key={i}>
                      <td>
                        <strong>{p.partyType}</strong>
                      </td>
                      <td>
                        {renderValue(p.firstName)} {renderValue(p.lastName)}
                      </td>
                      <td>{renderValue(p.companyName)}</td>
                      <td>{renderValue(p.country)}</td>
                      <td>
                        <Badge bg={p.isRegistered ? "success" : "warning"}>
                          {p.isRegistered ? "Registered" : "Unregistered"}
                        </Badge>
                      </td>
                      <td>
                        {p.claimantStatement ? (
                          <PopUpModalButton
                            title="View"
                            fileLink={p.claimantStatement}
                          />
                        ) : (
                          "NA"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div>No additional parties added yet</div>
            )}
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header>
            <strong>Scheduled Meetings</strong>
          </Card.Header>
          <Card.Body>
            {(() => {
              const meetings = (selectedData.timeTable?.sections || [])
                .flatMap((s) => s.meetings || [])
                .filter((m) => m?._id);
              return meetings.length > 0 ? (
                <Table bordered size="sm">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Title</th>
                      <th>Date & Time</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meetings.map((m, i) => (
                      <tr key={m._id}>
                        <td>{i + 1}</td>
                        <td>{m.meetingTitle || "Untitled"}</td>
                        <td>
                          {(() => {
                            const dateStr = new Date(
                              m.meetingDate
                            ).toLocaleDateString("en-US", {
                              timeZone: "Asia/Kolkata",
                              month: "short",
                              day: "2-digit",
                              year: "numeric",
                            });

                            const formatTime = (time24) => {
                              if (!time24) return "";
                              const [hours, minutes] = time24.split(":");
                              const hour = parseInt(hours, 10);
                              const ampm = hour >= 12 ? "PM" : "AM";
                              const hour12 = hour % 12 || 12;
                              return `${hour12}:${minutes} ${ampm}`;
                            };

                            return `${dateStr}, ${formatTime(m.meetingTime)}`;
                          })()}
                        </td>
                        <td>
                          <Badge
                            bg={
                              m.status === "COMPLETED" ? "success" : "primary"
                            }
                          >
                            {m.status}
                          </Badge>
                        </td>
                        <td>
                          <a
                            href={`/room/${m.meetingRoomId}?username=${user.firstName}(${selectedData.accessRole})`}
                            target="_blank"
                            className="btn btn-primary btn-sm"
                          >
                            Join
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted">No meetings scheduled yet.</p>
              );
            })()}
          </Card.Body>
        </Card>
        {isEditing && (
          <div className="text-center my-5">
            <Button
              variant="success"
              size="sm"
              onClick={handleSave}
              disabled={loading}
              className="px-5"
            >
              {loading ? <Spinner size="sm" /> : "Save & Resubmit for Approval"}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setIsEditing(false);
                setFormData(selectedData);
              }}
              className="ms-3 px-5"
            >
              Cancel
            </Button>
          </div>
        )}
      </Form>

      <AddPartyForm
        show={showAddPartyForm}
        onHide={() => setShowAddPartyForm(false)}
        onSave={() => onRefresh?.()}
        disputeId={selectedData._id}
        role={selectedData.accessRole}
      />
      <AddArbitratorForm
        show={showAddArbitratorForm}
        onHide={() => setShowAddArbitratorForm(false)}
        disputeId={selectedData._id}
        role={currentRole}
        onSave={() => onRefresh?.()}
      />
    </div>
  );
};

export default BindingDisputeDetails;
